import type { AnswerFlagRuleResult } from '@app/src/types/answerFlags'
import type { NaceCode, Provider } from '@app/src/types/organizations'
import type {
  AssessmentSection,
  AssessmentTemplate,
  Country,
  CountryVariant,
  RiskStatus,
  RiskType,
  SustainabilityArea,
} from '@app/src/types/resourceExplorer'

export interface SustainabilityTopic {
  id: number
  name: string
  sustainabilityArea: SustainabilityArea
  assessmentTemplateId?: number
  assessmentTemplate?: AssessmentTemplate
  assessmentTemplateSectionId?: number
  assessmentTemplateSection?: AssessmentSection
}

export interface SustainabilityTopicProviderRisk {
  id: number
  createdAt: string
  providerId: number
  provider?: Provider
  riskStatus: RiskStatus
  inherentRiskStatus?: RiskStatus
  answerFlagRiskStatus?: RiskStatus
  sustainabilityAreaRisks: SustainabilityAreaRisk[]
  answerFlagRuleResults?: AnswerFlagRuleResult[]
}

export interface SustainabilityAreaRisk {
  id: number
  createdAt: string
  providerRiskId: number
  sustainabilityArea: SustainabilityArea
  riskStatus: RiskStatus
  sustainabilityTopicRisks: SustainabilityTopicRisk[]
}

export interface SustainabilityTopicRisk {
  id: number
  createdAt: string
  providerId: number
  provider?: Provider
  sustainabilityTopicId: number
  sustainabilityTopic: SustainabilityTopic
  sustainabilityAreaRiskId: number
  totalRiskStatus: RiskStatus
  headquarterRiskStatus: RiskStatus
  operationRiskStatus: RiskStatus
  industryRiskStatus: RiskStatus
  /** Country and industry risk combined, before assessment results are applied. Absent on risks calculated before the column existed. */
  inherentRiskStatus?: RiskStatus
  assessmentTemplateId: number
  assessmentTemplate?: AssessmentTemplate
  assessmentLevel?: number
  /** Overall disclosure score (0-100) for the topic's linked assessment template. */
  assessmentScore?: number
  assessmentTemplateSectionId: number
  assessmentTemplateSection?: AssessmentSection
  assessmentSectionLevel?: number
  /** Per-section disclosure score (0-100) for the topic's linked assessment section. */
  assessmentSectionScore?: number
  assessmentTemplateLevels: number
  /**
   * Only set on topic risks stored while AI screenings under 30% coverage were left out of the
   * calculation. AI-screened risk is now capped at inherent risk instead, so new calculations never
   * set these; they stay until every provider has been recalculated.
   */
  isAssessmentExcludedDueToLowData?: boolean
  isAssessmentSectionExcludedDueToLowData?: boolean
  countryRisks?: SustainabilityTopicCountryRisk[]
  industryRisks?: SustainabilityTopicIndustryRisk[]
}

export interface SustainabilityTopicCountryRisk {
  id: number
  createdAt: string
  providerId: number
  provider?: Provider
  sustainabilityTopicRiskId: number
  SustainabilityTopic: SustainabilityTopic
  countryId: number
  country?: Country
  variant: CountryVariant
  riskTypeId: number
  riskType?: RiskType
  riskStatus: RiskStatus
}

export interface SustainabilityTopicIndustryRisk {
  id: number
  createdAt: string
  providerId: number
  provider?: Provider
  sustainabilityTopicRiskId: number
  SustainabilityTopic: SustainabilityTopic
  subNaceCodeId: number
  subNaceCode: NaceCode
  riskTypeId: number
  riskType?: RiskType
  riskStatus: RiskStatus
}

/**
 * Which value the risk distribution trend chart plots per month: count of
 * providers, or their spend. Mirrors the backend's TrendMetric enum.
 */
export enum TrendMetric {
  Count = 'Count',
  Spend = 'Spend',
}

/**
 * Which risk a distribution counts: the inherent risk from country and industry on its own, or the
 * overall risk once assessment results and answer flags are applied. The values are the fields the
 * request facets on.
 */
export enum RiskDimension {
  Inherent = 'inherentRiskStatus',
  Overall = 'riskStatus',
}

/**
 * Reason why a provider's risk was recalculated.
 * Inferred from snapshot differences or tracked by backend.
 */
export enum RiskChangeReason {
  /** First time risk was calculated (no previous snapshot) */
  InitialCalculation = 'InitialCalculation',
  /** Customer toggled risk indices on/off in settings */
  RiskTypesToggled = 'RiskTypesToggled',
  /** Worldfavor updated risk index data (country/industry risk values changed) */
  RiskTypesUpdated = 'RiskTypesUpdated',
  /** Customer enabled an assessment for topics */
  AssessmentEnabled = 'AssessmentEnabled',
  /** Customer disabled an assessment for topics */
  AssessmentDisabled = 'AssessmentDisabled',
  /** Assessment result deleted (inquiry/response removed for a period) */
  AssessmentDeleted = 'AssessmentDeleted',
  /** Assessment result changed (level updated) */
  AssessmentCalculated = 'AssessmentCalculated',
  /** Supplier's country was updated */
  CountryUpdated = 'CountryUpdated',
  /** Supplier's industry was updated */
  IndustryUpdated = 'IndustryUpdated',
  /** Supplier's country and industry were updated */
  CountryAndIndustryUpdated = 'CountryAndIndustryUpdated',
  /** Questionnaire response received with flagged answers */
  AnswerFlagged = 'AnswerFlagged',
  /** Risk reevaluated */
  MethodologyUpdated = 'MethodologyUpdated',
}

/**
 * Details computed by diffing provider risk snapshots.
 * Contains affected entity names and contextual information.
 */
export interface ProviderRiskReasonDetails {
  /** For RiskTypesUpdated - name of risk index that changed */
  riskIndexName?: string
  /** For RiskTypesUpdated - true if country risk, false if industry risk */
  isCountryRisk?: boolean
  /** For Assessment* reasons - name of assessment template */
  assessmentName?: string
  /** For ResponseSubmitted - name of questionnaire */
  questionnaireName?: string
  /** Names of sustainability topics affected by change */
  affectedTopicNames?: string[]
  /** For ResponseSubmitted - names of answer flag rules triggered */
  answerFlagNames?: string[]
}

/**
 * Translation data for risk reason descriptions.
 * Pure functions return this structure, components handle actual translation.
 */
export interface RiskReasonTranslation {
  /** Translation key for the risk reason description */
  messageKey: string
  /** Values to interpolate into the translated message */
  values: Record<string, string | string[]>
}
